import React, { useEffect, useState } from "react";
import SiteHeader from "../components/SiteHeader";
import Reveal from "../components/Reveal";
import AppPreview from "../components/AppPreview";
import {
  IconCheck,
  IconErrand,
  IconFood,
  IconGrocery,
  IconRide,
  MarkApple,
  MarkGooglePlay,
} from "../components/Icons";
import { ANDROID_STORE_URL, IOS_STORE_URL } from "../siteConfig";
import { useDocumentMeta } from "../useDocumentMeta";
import "../styles/download.css";

type Platform = "android" | "ios" | "other";

// QR image used on the download page (hosted)
const QR_IMAGE_URL = "https://pabs.online/qr.png";

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "other";

  const ua = navigator.userAgent || "";
  const uaLower = ua.toLowerCase();

  if (uaLower.includes("android")) return "android";

  // iPhone/iPad/iPod
  const isAppleMobile = /iphone|ipad|ipod/i.test(ua);
  if (isAppleMobile) return "ios";

  // iPadOS 13+ sometimes reports as Mac; detect via touch.
  const platform = (navigator.platform || "").toLowerCase();
  const maxTouchPoints = (navigator as any).maxTouchPoints as number | undefined;
  const isIpadOs = platform.includes("mac") && (maxTouchPoints ?? 0) > 1;
  if (isIpadOs) return "ios";

  return "other";
}

const HIGHLIGHTS = [
  { icon: <IconFood size={19} />, label: "Foods — meals and drinks from local restaurants and cafés" },
  { icon: <IconGrocery size={19} />, label: "Pabili — groceries, pharmacy items, and essentials" },
  { icon: <IconErrand size={19} />, label: "Pasugo — have a rider buy or bring something" },
  { icon: <IconRide size={19} />, label: "Angkas — a rider takes you across town" },
];

const DownloadPage: React.FC = () => {
  const [platform] = useState<Platform>(() => detectPlatform());

  useDocumentMeta({
    title: "Download the PABS app — iOS and Android",
    description:
      "Get the PABS app on iOS or Android to order food and groceries, send Pasugo errands, and book rides. Auto-detects your device and redirects to the right store.",
    canonicalPath: "/download",
    ogImage: "https://pabs.online/logo512.png",
  });

  useEffect(() => {
    if (platform === "android") {
      window.location.replace(ANDROID_STORE_URL);
      return;
    }

    if (platform === "ios") {
      window.location.replace(IOS_STORE_URL);
      return;
    }
  }, [platform]);

  const isRedirecting = platform === "android" || platform === "ios";
  const redirectUrl = platform === "android" ? ANDROID_STORE_URL : IOS_STORE_URL;
  const redirectLabel = platform === "android" ? "Google Play" : "the App Store";

  return (
    <>
      <SiteHeader current="/download" tone="ink" />

      <main id="main" className="dl">
        <section className="dl__hero">
          <div className="u-container dl__inner">
            <Reveal className="dl__copy">
              <span className="t-eyebrow">Mobile app</span>
              <h1 className="t-h1 dl__title">
                PABS, in your
                <br />
                <span className="t-grad">pocket.</span>
              </h1>
              <p className="t-lead dl__lead">
                Get the PABS app on iOS or Android. If you're on a phone, we'll
                send you straight to the right store.
              </p>

              <ul className="dl__highlights">
                {HIGHLIGHTS.map((h) => (
                  <li key={h.label}>
                    <span className="dl__hl-icon">{h.icon}</span>
                    {h.label}
                  </li>
                ))}
              </ul>

              <div className="dl__badges">
                <StoreBadge
                  href={IOS_STORE_URL}
                  targetLabel="App Store"
                  variant="ios"
                  smallText="Download on the"
                  bigText="App Store"
                />
                <StoreBadge
                  href={ANDROID_STORE_URL}
                  targetLabel="Google Play"
                  variant="android"
                  smallText="GET IT ON"
                  bigText="Google Play"
                />
              </div>

              {isRedirecting ? (
                <div className="dl__redirect" role="status">
                  <span className="pulse-dot" />
                  <span>
                    Redirecting you to {redirectLabel}…{" "}
                    <a href={redirectUrl} rel="nofollow noopener noreferrer">
                      Tap here
                    </a>{" "}
                    if nothing happens.
                  </span>
                </div>
              ) : (
                <QrCallout />
              )}
            </Reveal>

            <Reveal delay={140} className="dl__art">
              <AppPreview />
            </Reveal>
          </div>
        </section>
      </main>
    </>
  );
};

type StoreBadgeProps = {
  href: string;
  targetLabel: string;
  variant: "ios" | "android";
  smallText: string;
  bigText: string;
};

function StoreBadge({ href, targetLabel, variant, smallText, bigText }: StoreBadgeProps) {
  return (
    <a
      className={`store store--${variant}`}
      href={href}
      rel="nofollow noopener noreferrer"
      target="_blank"
      aria-label={`Open ${targetLabel}`}
    >
      <span className="store__icon" aria-hidden="true">
        {variant === "ios" ? <MarkApple size={26} /> : <MarkGooglePlay size={24} />}
      </span>
      <span className="store__text">
        <span className="store__small">{smallText}</span>
        <span className="store__big">{bigText}</span>
      </span>
    </a>
  );
}

function QrCallout() {
  const [useImage, setUseImage] = useState(true);

  return (
    <div className="qr">
      <div className="qr__box">
        {useImage ? (
          <img
            className="qr__img"
            src={QR_IMAGE_URL}
            alt="QR code linking to the PABS app download page"
            loading="lazy"
            onError={() => setUseImage(false)}
          />
        ) : (
          <QrPlaceholder />
        )}
      </div>
      <div className="qr__copy">
        <span className="badge">
          <IconCheck size={13} />
          Scan to install
        </span>
        <p>
          Point your phone camera at the code and we'll open the right store for
          your device.
        </p>
      </div>
    </div>
  );
}

function QrPlaceholder() {
  return (
    <svg
      className="qr__svg"
      width="92"
      height="92"
      viewBox="0 0 92 92"
      role="img"
      aria-label="QR code"
    >
      <rect x="0" y="0" width="92" height="92" fill="#fff" />
      <rect x="6" y="6" width="24" height="24" fill="#0b1220" />
      <rect x="10" y="10" width="16" height="16" fill="#fff" />
      <rect x="14" y="14" width="8" height="8" fill="#0b1220" />

      <rect x="62" y="6" width="24" height="24" fill="#0b1220" />
      <rect x="66" y="10" width="16" height="16" fill="#fff" />
      <rect x="70" y="14" width="8" height="8" fill="#0b1220" />

      <rect x="6" y="62" width="24" height="24" fill="#0b1220" />
      <rect x="10" y="66" width="16" height="16" fill="#fff" />
      <rect x="14" y="70" width="8" height="8" fill="#0b1220" />

      {/* pseudo QR noise */}
      <g fill="#0b1220">
        <rect x="40" y="14" width="6" height="6" />
        <rect x="48" y="14" width="6" height="6" />
        <rect x="40" y="22" width="6" height="6" />
        <rect x="52" y="26" width="6" height="6" />
        <rect x="36" y="34" width="6" height="6" />
        <rect x="44" y="34" width="6" height="6" />
        <rect x="52" y="34" width="6" height="6" />
        <rect x="40" y="42" width="6" height="6" />
        <rect x="56" y="42" width="6" height="6" />
        <rect x="36" y="50" width="6" height="6" />
        <rect x="48" y="50" width="6" height="6" />
        <rect x="56" y="50" width="6" height="6" />
        <rect x="40" y="58" width="6" height="6" />
        <rect x="52" y="58" width="6" height="6" />
        <rect x="36" y="66" width="6" height="6" />
        <rect x="44" y="70" width="6" height="6" />
        <rect x="52" y="70" width="6" height="6" />
        <rect x="60" y="66" width="6" height="6" />
      </g>

      <rect
        x="0.5"
        y="0.5"
        width="91"
        height="91"
        fill="none"
        stroke="rgba(11, 18, 32, 0.12)"
      />
    </svg>
  );
}

export default DownloadPage;
